import 'styles/repaircenter.scss';
import { Badge, Button, Divider, Grid, Icon, Input, Loader, Modal } from '@scuf/common';
import { LabelDescription } from 'components';
import { formatDate } from 'helpers';
import { RmaModelLiteMf, statusOptionsMf } from 'models';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useRecoilState, useSetRecoilState } from 'recoil';
import { COMPANY_MANAGER, REPAIR_CENTER, RMAMANUFATURA, Rma, companyDtoAtom, notificationAtom, user } from 'resources';
import { RmaLiteMf } from 'resources/atoms/rmaManufaturaAtom';
import useFetch from 'helpers/hooks/useFetch';
import { CompanyDTO } from 'models/userManager/addCustomer';
import { getColors } from 'helpers/getBadgeColors';
import { rmaStatusColorsMf, rmaTypeColorsMf } from 'models/rmamanufatura/detailsRma';
import requestFile from 'helpers/requestFile';
import Alert from 'components/alert';
import useUser from 'helpers/hooks/useUser';

const OrderAccordion: React.FC<any> = () => {
    const setNotification = useSetRecoilState(notificationAtom);
    const [rmaState, setRmaState] = useRecoilState<RmaModelLiteMf>(RmaLiteMf);
    const [loadingAction, setLoadingAction] = useState<boolean>(false);
    const [modalEdit, setModalEdit] = useState<boolean>(false);
    const { t } = useTranslation(['rmamanufatura', 'common', 'user']);
    const [newGbe, setNewGbe] = useState<string>('');
    const [loading, setLoading] = useState<boolean>(false);
    const { user: userDetails } = useUser();
    const [changes, setChanges] = useState({
        Invoice: '',
        InvoiceValue: ''
    });
    const [company, setCompany] = useRecoilState<CompanyDTO>(companyDtoAtom);
    const [_, getCompanyById, loadingCompany] = useFetch<{ id: number | undefined }, CompanyDTO>({
        url: COMPANY_MANAGER.getCompanyById,
        method: 'GET',
        onError: 'Error',
        redirectOnError: true
    });
    const [, updateInvoice, loader] = useFetch<RmaModelLiteMf>({
        url: REPAIR_CENTER.updateInvoice,
        method: 'POST',
        onError: 'Change',
        onSuccess: 'Change'
    });
    const [data, updateDataManufacturing] = useFetch<{ rmaid: number, gbe: string }>({
        url: RMAMANUFATURA.updateDataManufacturing,
        method: 'POST',
        onError: 'Change',
        onSuccess: 'Change'
    });

    useEffect(() => {
        setChanges({ Invoice: rmaState?.invoice, InvoiceValue: rmaState?.valorInvoice });
        if (rmaState?.companyId !== company?.id && rmaState?.companyId != null) {
            getCompanyInfo();
        }
    }, [rmaState]);

    useEffect(() => {
        if (modalEdit) {
            setNewGbe(rmaState.rmaItemsEquipmentManufacturing?.[0]?.gbe ?? '');
        }
    }, [modalEdit]);

    const handleChanges = async (): Promise<void> => {
        await updateInvoice(
            { ...rmaState, invoice: changes.Invoice, valorInvoice: changes.InvoiceValue ? changes.InvoiceValue : '0' },
            _ => {
                setRmaState(old => ({ ...old, invoice: changes.Invoice, valorInvoice: changes.InvoiceValue }));
            }
        );
    };

    const validate = (): boolean => {
        return (
            (changes.Invoice !== rmaState?.invoice || changes.InvoiceValue !== rmaState?.valorInvoice) &&
            rmaState?.statusId !== 10
        );
    };

    const getCompanyInfo = async (): Promise<void> => {
        await getCompanyById({ id: rmaState?.companyId }, current => {
            setCompany(current.data!);
        });
    };

    const getStatusInfo = (statusId?: number) => {
        const status = statusOptionsMf.find(option => option.value === statusId);
        return status ?? { text: 'Desconhecido' };
    };

    const handlePrint = async () => {
        setLoadingAction(true);
        try {
            await requestFile.print(REPAIR_CENTER.generatePdfFromRma, {
                method: 'GET',
                params: { id: rmaState?.id }
            });
            setLoadingAction(false);
        } catch {
            setNotification(old => ({ ...old, message: 'UploadError' }));
            setLoadingAction(false);
        }
    };

    const sendUpdateData = async () => {
        setLoading(true);
        try {
            await updateDataManufacturing({ rmaid: rmaState.id, gbe: newGbe })
            setLoading(false);
            setModalEdit(false);
            setRmaState(prev => ({
                ...prev, rmaItemsEquipmentManufacturing: prev.rmaItemsEquipmentManufacturing
                    .map(item => ({ ...item, gbe: newGbe }))
            }));
            setNotification(old => ({ ...old, message: t('common:actions.ChangeDataSucess'), type: 'success' }));
        } catch (error) {
            setNotification(old => ({ ...old, message: t('common:actions.ChangeDataFaill') }));
            setLoading(false);
        }
    }

    const showAlertAddress = () => {
        const shipping = rmaState?.shipping;
        const situation = rmaState?.situation;
        const statusId = rmaState?.statusId;

        const isValidShipping = shipping === 'ENVIO PELA HONEYWELL' || shipping === 'ENVIO PELO CLIENTE' || 'COLETA TRANSPORTADORA';
        const isApprovedSituation = situation === 'APROVADO';
        const isAwaitingCustomerStatus = statusId === 54;

        return (isValidShipping && isApprovedSituation && isAwaitingCustomerStatus);
    };

    function editModal(): JSX.Element {
        return (
            <Modal
                onClose={() => setModalEdit(false)}
                size="small"
                open={modalEdit}
                closeOnDimmerClick={false}
                closeOnDocumentClick={false}
            >
                <Modal.Header>{t('common:actions.ChangeData')}</Modal.Header>
                <Modal.Content>
                    <div className="approval-modal-content">
                        <Input
                            fluid
                            placeholder="GBE"
                            label="GBE"
                            value={newGbe}
                            onChange={(value: string) => {
                                setNewGbe(value);
                            }}
                        />
                    </div>
                </Modal.Content>
                <Modal.Footer>
                    <Button content={t("common:actions.Cancel")} type="secondary" onClick={() => setModalEdit(false)} size="medium" />
                    <Button
                        content={t("common:actions.Save")}
                        loading={loading}
                        onClick={() => sendUpdateData()}
                        size="medium"
                    />
                </Modal.Footer>
            </Modal>
        );
    }

    return (
        <>
            {editModal()}
            {loadingCompany ? (
                <Loader text={t('common:actions.LoadingOrder')} />
            ) : (
                <Grid>
                    <h3>{t('common:translation.General')}</h3>
                    <Grid.Row>
                        <Grid.Column className="grid-column" width={3} mWidth={6} sWidth={12}>
                            <LabelDescription title={t('rma.order.RmaNumber')} value={rmaState?.id} />
                            <LabelDescription title={t('rma.order.RmaDate')} value={formatDate(rmaState?.createdAt)} />
                            <LabelDescription
                                title={t('common:translation.Status')}
                                value={
                                    <Badge color={getColors(rmaState?.statusId, rmaStatusColorsMf)}>
                                        {getStatusInfo(rmaState?.statusId).text}
                                    </Badge>
                                }
                            />
                        </Grid.Column>
                        <Grid.Column className="grid-column" width={3} mWidth={6} sWidth={12}>
                            <LabelDescription title={t('rmamanufatura:translation.NFHoneywell')} value={rmaState?.rmaItemsEquipmentManufacturing?.[0]?.nfHoneywell} />
                            <LabelDescription title={t('common:status.Situation')} value={rmaState?.situation ?? '-'} />
                            <LabelDescription title={t('rma.shipping.Shipping')} value={rmaState?.shipping ?? '-'} />
                        </Grid.Column>
                        <Grid.Column className="grid-column" width={3} mWidth={6} sWidth={12}>
                            <LabelDescription title={'GBE'} value={rmaState.rmaItemsEquipmentManufacturing?.[0]?.gbe} />
                            {/* mais dados podem ser colocados aqui */}
                        </Grid.Column>

                        <Grid.Column className="grid-column" width={3} mWidth={6} sWidth={12}>
                            {showAlertAddress() ? (
                                <div style={{ flex: 1, minWidth: '220px' }}>
                                    <Alert text={t('rmamanufatura:translation.RequestAddress')} />
                                </div>
                            ) : null}
                        </Grid.Column>
                    </Grid.Row>
                </Grid>
            )}
            <div className="buttonsSearch">
                {validate() && (
                    <Button
                        size="small"
                        icon={<Icon name="save-as" root="common" />}
                        content={t('common:actions.Save')}
                        loading={loader}
                        onClick={handleChanges}
                    ></Button>
                )}
                {userDetails?.role === '1' || userDetails?.role === '2' ? (


                    <Button
                        icon={<Icon name="edit" root="common" />}
                        iconPosition="left"
                        //loading={loadingAction}
                        size="small"
                        onClick={() => setModalEdit(true)}
                        content={t('common:translation.Edit')}
                        type="primary"
                    />
                ) : ('')}
                {/* <Button
                    icon={<Icon name="print" root="common" />}
                    iconPosition="left"
                    loading={loadingAction}
                    size="small"
                    onClick={() => handlePrint()}
                    content={t('common:actions.Print')}
                    type="primary"
                /> */}
            </div>
            <Divider></Divider>
            <Grid>
                <h3>{t('register:type.Company')}</h3>
                <Grid.Row>
                    <Grid.Column className="grid-column" width={6} sWidth={12}>
                        <LabelDescription title={t('CNPJ')} value={company?.cnpj} />
                        <LabelDescription title={t('user:company.Name')} value={company?.name} />
                        {/* <LabelDescription title={t('rma.order.Customer.InvoiceCNPJ')} value={rmaState?.cnpjNota} /> */}
                        {/* <LabelDescription
                            title={t('rma.order.Customer.StatusCNPJ')}
                            value={
                                rmaState?.statusCnpjCompany === 1
                                    ? 'INATIVO'
                                    : rmaState?.statusCnpjCompany === 2
                                    ? 'ATIVO'
                                    : 'INVÁLIDO'
                            }
                        />
                        <LabelDescription
                            title={t('rma.order.Customer.StatusInvoiceCNPJ')}
                            value={
                                rmaState?.statusCnpjNota === 1 ? 'INATIVO' : rmaState?.statusCnpjNota === 2 ? 'ATIVO' : 'INVÁLIDO'
                            }
                        /> */}
                    </Grid.Column>
                    <Grid.Column className="grid-column" width={6} sWidth={12}>

                        <LabelDescription
                            title={t('rmamanufatura:translation.ResponsibleName')}
                            value={rmaState.nameResponsible}
                        />
                        <LabelDescription
                            title={t('rmamanufatura:translation.ResponsibleEmail')}
                            value={company?.users?.find(x => x.ssoId === rmaState.ssoId)?.email}
                        />
                        <LabelDescription
                            title={t('rmamanufatura:translation.ResponsiblePhone')}
                            value={rmaState.phoneResponsible}
                        />
                    </Grid.Column>
                </Grid.Row>
            </Grid>
        </>
    );
};

export default OrderAccordion;
